import {
  Button,
  Flex,
  Modal,
  NumberField,
  Typography,
} from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import type { ProductThreshold } from '@iheartjane/types';
import capitalize from 'lodash-es/capitalize';
import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ConditionType } from '@jane/business-admin/components/shared/specials/modal/form';
import { FORM_FIELD_DEFAULT_MARGIN } from '@jane/business-admin/components/shared/specials/modal/form';
import { useGlobalSpecialsModal } from '@jane/business-admin/providers/GlobalSpecialsModalProvider';
import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';

interface Props {
  conditions: ProductThreshold;
  fieldPrefix: string;
  onConditionsUpdate: (conditions: ProductThreshold) => void;
  type: ConditionType.Price;
}

/**
 * Component for each condition line item in building conditions for a Special. This component is hardcoded with two inputs for price conditions.
 * @param params.conditions Min and max price conditions
 * @param params.fieldPrefix Used for input name fields, to prevent duplicates when there are two conditions cards in the case of BOGO specials
 * @param params.onConditionsUpdate Callback when price conditions are changed in the inputs
 * @param params.type Type of condition
 */
export const PriceConditions = ({
  conditions: conditionsProp,
  fieldPrefix,
  onConditionsUpdate,
  type,
}: Props) => {
  const { setError, clearErrors, trigger } = useFormContext();
  const {
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { isDisabled: globalSpecialDisabled } = useGlobalSpecialsModal();

  const [conditions, setConditions] = useState(conditionsProp);
  const [validationError, setValidationError] = useState<string>();
  const emptyConditions = useMemo(() => {
    return (
      isEmpty(conditionsProp) ||
      (conditionsProp.maximum_price === 0 && conditionsProp.minimum_price === 0)
    );
  }, [conditionsProp]);
  const [expanded, setExpanded] = useState(!emptyConditions);

  useEffect(() => {
    setExpanded(!emptyConditions);
    setConditions(conditionsProp);
  }, [JSON.stringify(conditionsProp)]);

  const removeCondition = () => {
    setValidationError(undefined);
    clearErrors('price-error');
    trigger();

    setConditions({});
    setExpanded(false);

    onConditionsUpdate({});
  };

  const validateFields = (
    type: 'maximum_price' | 'minimum_price',
    value: number | string | undefined,
    conditions: ProductThreshold
  ) => {
    setValidationError(undefined);
    clearErrors('price-error');
    trigger();

    const oppositeType =
      type === 'maximum_price' ? 'minimum_price' : 'maximum_price';

    if (Number(value) === 0 && Number(conditions[oppositeType]) === 0) {
      trigger();
    } else if (
      type === 'minimum_price' &&
      Number(value) > Number(conditions[oppositeType])
    ) {
      setError('price-error', { message: 'Price error' });
      setValidationError('Minimum price must be less than maximum price');
    } else if (
      type === 'maximum_price' &&
      Number(value) < Number(conditions[oppositeType])
    ) {
      setError('price-error', { message: 'Price error' });
      setValidationError('Maximum price must be greater than minimum price');
    }
  };

  const onChange = useCallback(
    (
      type: 'maximum_price' | 'minimum_price',
      value: number | string | undefined
    ) => {
      validateFields(type, value, conditions);

      let updatedValues = Object.create({
        ...conditions,
      });
      if (value === undefined || value === '') {
        updatedValues = omit(updatedValues, type);
      } else {
        updatedValues = {
          ...conditions,
          [type]: value,
        };
      }
      onConditionsUpdate(updatedValues);
    },
    [JSON.stringify(conditions)]
  );

  const isAnyPosSynced = isJanePosSynced || posSynced;
  const isDisabled = isAnyPosSynced || isReadOnly || globalSpecialDisabled;

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb={FORM_FIELD_DEFAULT_MARGIN}
      >
        <Typography variant="body-semibold">{capitalize(type)}</Typography>
        <Flex gap={24}>
          <Button
            label={expanded ? 'Remove condition' : 'Add condition'}
            onClick={() => {
              expanded ? removeCondition() : setExpanded(true);
            }}
            variant={expanded ? 'tertiary' : 'secondary'}
            disabled={isDisabled}
          />
        </Flex>
      </Flex>
      {expanded && (
        <>
          <Flex gap={24}>
            {/* TODO: For some reason DollarInput works better here with allowedDecimalPlaces,
                but it also contains a Form.NumberField which we don't want here */}
            <NumberField
              startUnit="$"
              width="100%"
              step={0.01}
              allowedDecimalPlaces={2}
              name={`${fieldPrefix}.rules.price_minimum`}
              label="Price minimum"
              defaultValue={conditions.minimum_price || undefined}
              onChange={(value) => onChange('minimum_price', value)}
              disabled={isDisabled}
              disableOnWheel
            />
            {/* TODO: For some reason DollarInput works better here with allowedDecimalPlaces,
                but it also contains a Form.NumberField which we don't want here */}
            <NumberField
              startUnit="$"
              width="100%"
              step={0.01}
              allowedDecimalPlaces={2}
              name={`${fieldPrefix}.rules.price_maximum`}
              label="Price maximum"
              defaultValue={conditions.maximum_price || undefined}
              onChange={(value) => onChange('maximum_price', value)}
              disabled={isDisabled}
              disableOnWheel
            />
          </Flex>
          {validationError && (
            <Typography color="error" mt={12}>
              {validationError}
            </Typography>
          )}
          <Modal.ContentDivider />
        </>
      )}
    </>
  );
};
